import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { printTicket } from "@/lib/ticket";
import { CHF } from "@/lib/apiClient";
import { Printer, Trash2, Truck, ShoppingBag, Clock, CalendarClock } from "lucide-react";
import { STATUS_LABELS, STATUS_COLOR, normalizeStatus } from "@/lib/statuses";

const STATUS_OPTIONS = (mode) => ["pending", "confirmed", mode === "delivery" ? "en_route" : "ready", "completed", "refused", "cancelled"];
const rank = (s) => (s === "pending" ? 0 : ["completed", "cancelled", "refused"].includes(s) ? 2 : 1);
const fmtReceived = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return ""; }
};

export default function OrdersTab({ settings, onPending }) {
  const [allOrders, setAllOrders] = useState([]);
  const prevPending = useRef(0);

  const load = useCallback(async () => {
    const { data } = await api.get("/orders", { params: { status: "all" } });
    setAllOrders(data);
  }, []);

  const pollPending = useCallback(async () => {
    try {
      const { data } = await api.get("/orders/pending-count");
      onPending?.(data.count, prevPending.current);
      prevPending.current = data.count;
    } catch {}
  }, [onPending]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    pollPending();
    const t = setInterval(() => { load(); pollPending(); }, 8000);
    return () => clearInterval(t);
  }, [load, pollPending]);

  const setStatus = async (o, status) => {
    setAllOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status } : x)));
    try {
      await api.put(`/orders/${o.id}/status`, { status });
      toast.success(`Commande #${o.order_number} → ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("Erreur");
    }
    load(); pollPending();
  };

  const del = async (o) => {
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Commande supprimée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const orders = [...allOrders].sort((a, b) => {
    const r = rank(a.status) - rank(b.status);
    return r !== 0 ? r : b.order_number - a.order_number;
  });

  const isTerminal = (s) => ["completed", "cancelled", "refused"].includes(s);

  return (
    <div>
      <p className="text-sm text-white/50 mb-4">Toutes les commandes ({allOrders.length}) — les nouvelles apparaissent en haut. Changez le statut directement dans le menu déroulant de chaque commande.</p>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {orders.length === 0 && <p className="text-white/50 col-span-full py-10 text-center">Aucune commande.</p>}
        {orders.map((o) => (
          <div key={o.id} className={`border-2 bg-neutral-900 p-4 ${o.status === "pending" ? "border-brand-amber ring-2 ring-brand-amber/40" : isTerminal(o.status) ? "border-white/10 opacity-70" : "border-white/15"}`} data-testid={`admin-order-${o.order_number}`}>
            <div className="flex items-center justify-between">
              <span className="font-display text-3xl font-extrabold">#{o.order_number}{o.status === "pending" && <span className="ml-2 align-middle text-[10px] font-sans font-bold bg-brand-amber text-black px-2 py-0.5">NOUVELLE</span>}</span>
              <span className={`text-xs font-bold px-2 py-1 ${STATUS_COLOR[o.status]}`} data-testid={`admin-status-${o.order_number}`}>{STATUS_LABELS[o.status]}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-white/60 mt-1">
              <span className="flex items-center gap-1">{o.mode === "delivery" ? <Truck className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}{o.mode === "delivery" ? "Livraison" : "À emporter"}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{o.timeslot}</span>
            </div>
            <p className="text-xs text-white/50 mt-1 flex items-center gap-1" data-testid={`order-received-${o.order_number}`}>
              <CalendarClock className="w-3 h-3" /> Reçue le {fmtReceived(o.created_at)}
            </p>
            <p className="text-sm mt-2 font-medium">{o.customer?.name} · {o.customer?.phone}</p>
            {o.mode === "delivery" && <p className="text-xs text-white/60">{o.customer?.address} {o.customer?.postal_code}</p>}

            <div className="mt-3 border-t border-white/10 pt-2 space-y-1 text-sm">
              {o.items.map((i, idx) => (
                <div key={idx}>
                  <div className="flex justify-between"><span>{i.qty}× {i.name}</span></div>
                  {i.selected_addons?.map((a, j) => <div key={j} className="text-xs text-white/50 pl-3">+ {a.name}</div>)}
                  {i.comment && <div className="text-xs italic text-brand-amber pl-3">» {i.comment}</div>}
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center mt-3 pt-2 border-t border-white/10">
              <span className="font-bold text-lg">{CHF(o.total)}</span>
              <span className="text-xs text-white/50">dont TVA {o.vat_rate}% : {CHF(o.vat_amount)}</span>
            </div>

            <div className="flex items-center gap-2 mt-3">
              <select
                value={normalizeStatus(o.status)}
                onChange={(e) => setStatus(o, e.target.value)}
                data-testid={`status-select-${o.order_number}`}
                className={`flex-1 rounded-none h-11 px-2 text-sm font-bold border-2 cursor-pointer ${o.status === "pending" ? "bg-brand-amber text-black border-brand-amber" : "bg-neutral-800 text-white border-white/30"}`}
              >
                {STATUS_OPTIONS(o.mode).map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
              <Button onClick={() => printTicket(o, settings)} variant="outline" data-testid={`reprint-${o.order_number}`} className="rounded-none border-white/30 bg-transparent hover:bg-white/10 h-11 w-11 p-0"><Printer className="w-4 h-4" /></Button>
              {isTerminal(o.status) && (
                <Button onClick={() => del(o)} variant="outline" data-testid={`delete-${o.order_number}`} className="rounded-none border-red-500/50 text-red-400 bg-transparent hover:bg-red-500/10 h-11 w-11 p-0"><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
